'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { PageSeo } from '@/components/seo/PageSeo';
import FairtradeHero from '@/components/help-center/fairtrade/FairtradeHero';
import FairtradeToolbar from '@/components/help-center/fairtrade/FairtradeToolbar';
import FairtradeTable from '@/components/help-center/fairtrade/FairtradeTable';
import FairtradeMobileList from '@/components/help-center/fairtrade/FairtradeMobileList';
import FairtradePagination from '@/components/help-center/fairtrade/FairtradePagination';
import FairtradePageSizeSelect from '@/components/help-center/fairtrade/FairtradePageSizeSelect';
import PowerJourneyDialog from '@/components/help-center/fairtrade/PowerJourneyDialog';
import {
  FairtradeEmptyState,
  FairtradeErrorState,
  FairtradeLoadingSkeleton,
} from '@/components/help-center/fairtrade/FairtradeStates';
import FairtradeCategoryCards from '@/components/help-center/fairtrade/FairtradeCategoryCards';
import FairtradeSortSelect from '@/components/help-center/fairtrade/FairtradeSortSelect';
import FairtradeStatusFilterBar from '@/components/help-center/fairtrade/FairtradeStatusFilterBar';
import {
  FairtradeDarkPanelShell,
  fairtradeDarkDividerClass,
} from '@/components/help-center/fairtrade/fairtrade-dark-panel';
import { PowerCollectionProvider } from '@/contexts/PowerCollectionContext';
import { FairtradeWishlistToastHost } from '@/components/help-center/fairtrade/FairtradeWishlistToast';
import {
  computeFairtradeCategoryStats,
  buildFairtradeCollectionPriceIndex,
  isFairtradeCollectionHub,
  pickFeaturedFairtradePower,
  type FairtradePowerRow,
} from '@/lib/fairtrade-catalog';
import {
  DEFAULT_FAIRTRADE_QUERY,
  filterAndSortFairtradeRows,
  hasActiveFairtradeFilters,
  paginateFairtradeRows,
  type FairtradeQueryState,
} from '@/lib/fairtrade-query';
import { Eraser } from 'lucide-react';
import {
  clearFairtradeCatalogCache,
  fetchFairtradeCatalog,
} from '@/services/fairtrade.service';
import { syncPriceActivityFromCatalog } from '@/services/priceActivity.service';
import { useHelpAppearance } from '@/lib/help-appearance';
import { cn } from '@/lib/utils';

export default function FairtradePage() {
  const [rows, setRows] = useState<FairtradePowerRow[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState<FairtradeQueryState>(DEFAULT_FAIRTRADE_QUERY);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [priceActivityTick, setPriceActivityTick] = useState(0);
  const [logoReady, setLogoReady] = useState(false);
  const { appearance, setMode, hydrated, isLight } = useHelpAppearance();
  const isDark = hydrated ? appearance === 'dark' : true;

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(false);
    try {
      if (force) clearFairtradeCatalogCache();
      const data = await fetchFairtradeCatalog(undefined, { force });
      setRows(data);
      await syncPriceActivityFromCatalog(data);
      setPriceActivityTick((n) => n + 1);
    } catch {
      setError(true);
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    const markReady = () => {
      if (!cancelled) setLogoReady(true);
    };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = '/icons/tradelogo.webp';
    if (img.complete) markReady();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(false);

    void fetchFairtradeCatalog()
      .then(async (data) => {
        if (cancelled) return;
        setRows(data);
        await syncPriceActivityFromCatalog(data);
        if (!cancelled) setPriceActivityTick((n) => n + 1);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setRows(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const patchQuery = useCallback((patch: Partial<FairtradeQueryState>) => {
    setQuery((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearFilters = useCallback(() => {
    setQuery((prev) => ({
      ...DEFAULT_FAIRTRADE_QUERY,
      pageSize: prev.pageSize,
      page: 1,
    }));
    requestAnimationFrame(() => {
      document
        .getElementById('fairtrade-powers-panel')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const categoryStats = useMemo(
    () => (rows ? computeFairtradeCategoryStats(rows) : []),
    [rows]
  );

  const collectionPrices = useMemo(
    () => (rows ? buildFairtradeCollectionPriceIndex(rows) : undefined),
    [rows]
  );

  const catalogListTotal = useMemo(
    () => (rows ? rows.filter((r) => !isFairtradeCollectionHub(r)).length : 0),
    [rows]
  );

  const latestPowerId = useMemo(() => {
    if (!rows?.length) return null;
    const featured = pickFeaturedFairtradePower(rows);
    if (featured) return featured.id;
    return Math.max(...rows.map((r) => r.id));
  }, [rows]);

  const filtered = useMemo(
    () => (rows ? filterAndSortFairtradeRows(rows, query, collectionPrices) : []),
    [rows, query, collectionPrices]
  );

  const { pageItems, total, totalPages, safePage, rangeStart, rangeEnd } = useMemo(
    () => paginateFairtradeRows(filtered, query.page, query.pageSize),
    [filtered, query.page, query.pageSize]
  );

  useEffect(() => {
    if (query.page !== safePage) {
      setQuery((prev) => ({ ...prev, page: safePage }));
    }
  }, [query.page, safePage]);

  const retry = () => {
    void load(true);
  };

  const listBlock = (
    <div className="bg-transparent">
      <div className="flex flex-col gap-3 px-4 py-4 md:px-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <FairtradeStatusFilterBar
            value={query.status}
            onChange={(status) => patchQuery({ status, page: 1 })}
            isDark
          />
          {hasActiveFairtradeFilters(query) || query.sort !== DEFAULT_FAIRTRADE_QUERY.sort ? (
            <button
              type="button"
              onClick={clearFilters}
              className={cn(
                'inline-flex w-fit items-center gap-1.5 rounded-2xl border px-3 py-1.5',
                'font-poppins text-[11px] font-semibold uppercase tracking-wide',
                'transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
                'border-rose-400/30 bg-rose-500/10 text-rose-100',
                'hover:border-rose-300/55 hover:bg-rose-400/25 hover:text-white',
                'hover:shadow-[0_0_16px_rgba(244,63,94,0.28)]',
                'active:scale-[0.98]'
              )}
            >
              <Eraser className="h-3.5 w-3.5" aria-hidden />
              Limpar filtros
            </button>
          ) : null}
        </div>

        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <FairtradePageSizeSelect
            pageSize={query.pageSize}
            onPageSizeChange={(pageSize) => patchQuery({ pageSize, page: 1 })}
            isDark
          />
          <div className="md:hidden">
            <FairtradeSortSelect
              value={query.sort}
              onChange={(sort) => patchQuery({ sort, page: 1 })}
              isDark
            />
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div className="px-4 pb-5 md:px-5">
          <FairtradeEmptyState onClear={clearFilters} isDark />
        </div>
      ) : (
        <>
          <FairtradeTable
            rows={pageItems}
            isDark
            activeFilter={query.type}
            latestPowerId={latestPowerId}
            sort={query.sort}
            onSortChange={(sort) => patchQuery({ sort, page: 1 })}
            collectionPrices={collectionPrices}
          />
          <div className="px-4 pb-2 md:hidden md:px-5">
            <FairtradeMobileList
              rows={pageItems}
              isDark
              activeFilter={query.type}
              latestPowerId={latestPowerId}
              collectionPrices={collectionPrices}
            />
          </div>
          <FairtradePagination
            page={safePage}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onPageChange={(page) => patchQuery({ page })}
            isDark
          />
        </>
      )}
    </div>
  );

  const catalogPending = loading && !rows && !error;
  const pageReady = logoReady && !catalogPending;

  return (
    <PowerCollectionProvider catalogRows={rows} collectionPrices={collectionPrices}>
      <div className={cn('flex min-h-screen flex-col', isLight ? 'bg-slate-50' : 'bg-white')}>
        <PageSeo path="/fairtrade" />
        <Navbar />
        <main
          className={cn(
            'flex-1 transition-colors duration-300',
            isLight ? 'bg-[#F4F7FC] text-gray-900' : 'bg-[#030023] text-sky-50'
          )}
        >
          {!pageReady ? (
            <FairtradeLoadingSkeleton isDark={isDark} />
          ) : (
            <>
              <FairtradeHero
                appearance={appearance}
                onAppearanceChange={setMode}
                isLight={isLight}
              />

              {error && !rows ? (
                <FairtradeErrorState onRetry={retry} isDark={isDark} />
              ) : (
                <div className="mx-auto max-w-7xl space-y-6 px-6 pb-8 pt-3 sm:pb-10 sm:pt-4 lg:pb-12 lg:pt-5">
                  <FairtradeCategoryCards
                    stats={categoryStats}
                    activeType={query.type}
                    priceActivityTick={priceActivityTick}
                    onSelectType={(type) => {
                      patchQuery({ type, page: 1 });
                      requestAnimationFrame(() => {
                        document
                          .getElementById('fairtrade-powers-panel')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }}
                    isDark={isDark}
                  />

                  <FairtradeDarkPanelShell
                    id="fairtrade-powers-panel"
                    className="overflow-hidden scroll-mt-24 text-sky-50"
                    innerClassName="p-0"
                  >
                    <FairtradeToolbar
                      state={query}
                      catalogTotal={catalogListTotal}
                      onOpenWishlist={() => setJourneyOpen(true)}
                      onChange={patchQuery}
                      embedded
                      isDark
                    />
                    <div className={cn('border-t', fairtradeDarkDividerClass)}>{listBlock}</div>
                  </FairtradeDarkPanelShell>
                </div>
              )}
            </>
          )}
        </main>
        <Footer />

        <PowerJourneyDialog
          open={journeyOpen}
          onOpenChange={setJourneyOpen}
          catalogRows={rows ?? []}
          collectionPrices={collectionPrices}
        />
        <FairtradeWishlistToastHost />
      </div>
    </PowerCollectionProvider>
  );
}
